import React, { useEffect, useState, useCallback } from 'react';
import api from '../utils/api';
import toast from 'react-hot-toast';
import { Plus, Pencil, Trash2, Search, Phone, User } from 'lucide-react';
import { Modal } from './Tables';

const EMPTY = { name: '', mobile: '' };

export default function Customers() {
  const [customers, setCustomers] = useState([]);
  const [modal,    setModal]      = useState(null);
  const [selected, setSelected]   = useState(null);
  const [form,     setForm]       = useState(EMPTY);
  const [search,   setSearch]     = useState('');

  const load = useCallback(async () => {
    try {
      const res = await api.get('/customers' + (search ? `?q=${encodeURIComponent(search)}` : ''));
      setCustomers(Array.isArray(res.data) ? res.data : []);
    } catch { toast.error('Failed to load customers'); }
  }, [search]);

  useEffect(() => { load(); }, [load]);

  const openAdd  = () => { setForm(EMPTY); setModal('add'); };
  const openEdit = (c) => { setSelected(c); setForm({ name: c.name, mobile: c.mobile }); setModal('edit'); };

  const save = async () => {
    if (!form.name.trim())   return toast.error('Name is required');
    if (!form.mobile.trim()) return toast.error('Mobile number is required');
    try {
      if (modal === 'add') {
        await api.post('/customers', form);
        toast.success('Customer added');
      } else {
        await api.put('/customers?id=' + selected.id, form);
        toast.success('Customer updated');
      }
      setModal(null); load();
    } catch (e) { toast.error(e.response?.data?.error || 'Error'); }
  };

  const del = async (c) => {
    if (!window.confirm(`Delete ${c.name}?`)) return;
    try {
      await api.delete('/customers?id=' + c.id);
      toast.success('Customer deleted'); load();
    } catch (e) { toast.error(e.response?.data?.error || 'Error'); }
  };

  const occupied = customers.filter(c => c.is_occupied);
  const free     = customers.filter(c => !c.is_occupied);

  return (
    <div>
      <div className="page-header">
        <h2 className="page-title">Customers</h2>
        <button className="btn btn-primary" onClick={openAdd}><Plus size={16}/> Add Customer</button>
      </div>

      {/* Stats */}
      <div className="inv-stats-row" style={{ marginBottom: 16 }}>
        <div className="inv-stat"><div className="inv-stat-value">{customers.length}</div><div className="inv-stat-label">Total</div></div>
        <div className="inv-stat"><div className="inv-stat-value" style={{ color:'#22c55e' }}>{free.length}</div><div className="inv-stat-label">Available</div></div>
        <div className="inv-stat"><div className="inv-stat-value" style={{ color:'#f97316' }}>{occupied.length}</div><div className="inv-stat-label">Playing</div></div>
      </div>

      {/* Search */}
      <div style={{ marginBottom: 12, position:'relative' }}>
        <Search size={16} style={{ position:'absolute', left:12, top:'50%', transform:'translateY(-50%)', color:'#94a3b8' }}/>
        <input className="search-input" style={{ paddingLeft:36, maxWidth:360 }}
          placeholder="Search by name or mobile…"
          value={search} onChange={e => setSearch(e.target.value)}/>
      </div>

      <div className="card">
        <table className="data-table">
          <thead>
            <tr><th>Name</th><th>Mobile</th><th>Status</th><th>Actions</th></tr>
          </thead>
          <tbody>
            {customers.map(c => (
              <tr key={c.id} style={{ background: c.is_occupied ? '#fff7ed' : '' }}>
                <td>
                  <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                    <div style={{ width:32, height:32, borderRadius:'50%', background: c.is_occupied ? '#fed7aa' : '#dbeafe', display:'flex', alignItems:'center', justifyContent:'center', fontWeight:700, fontSize:14, color: c.is_occupied ? '#c2410c' : '#1d4ed8', flexShrink:0 }}>
                      {c.name[0].toUpperCase()}
                    </div>
                    <strong>{c.name}</strong>
                  </div>
                </td>
                <td><div style={{ display:'flex', alignItems:'center', gap:6 }}><Phone size={13} color="#94a3b8"/>{c.mobile}</div></td>
                <td>
                  {c.is_occupied
                    ? <span className="badge badge-paused">🎱 Playing</span>
                    : <span className="badge badge-active">✓ Available</span>}
                </td>
                <td>
                  <div style={{ display:'flex', gap:6 }}>
                    <button className="btn btn-outline btn-sm" onClick={() => openEdit(c)}><Pencil size={13}/></button>
                    <button className="btn btn-danger btn-sm" onClick={() => del(c)} disabled={c.is_occupied}><Trash2 size={13}/></button>
                  </div>
                </td>
              </tr>
            ))}
            {customers.length === 0 && (
              <tr><td colSpan={4} style={{ textAlign:'center', color:'#94a3b8', padding:40 }}>
                No customers yet — click "Add Customer"
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {(modal === 'add' || modal === 'edit') && (
        <Modal title={modal === 'add' ? 'Add Customer' : 'Edit Customer'} onClose={() => setModal(null)}>
          <div className="form-group">
            <label>Full Name</label>
            <input autoFocus value={form.name}
              onChange={e => setForm(f => ({...f, name: e.target.value}))}
              placeholder="e.g. Rahul Sharma"/>
          </div>
          <div className="form-group">
            <label>Mobile Number (unique)</label>
            <input type="tel" value={form.mobile}
              onChange={e => setForm(f => ({...f, mobile: e.target.value.replace(/\D/,'')})) }
              placeholder="10-digit mobile number" maxLength={15}/>
            <div style={{ fontSize:12, color:'#94a3b8', marginTop:4 }}>
              Mobile is used as unique ID — two players with same name can be differentiated
            </div>
          </div>
          <div className="modal-actions">
            <button className="btn btn-outline" onClick={() => setModal(null)}>Cancel</button>
            <button className="btn btn-primary" onClick={save}>Save</button>
          </div>
        </Modal>
      )}
    </div>
  );
}
